import { useMemo, useState, useEffect, useCallback, memo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Typography, makeStyles } from '@material-ui/core';
import PageContainer from '../../shared/pageContainer';
import Filters from '../../shared/filters';
import NotificationCard from '../../shared/notificationCard';
import GridTable from '../../shared/gridTable';
import TextBoxWithTooltip from '../../shared/textBoxWithTooltip';
import SurveyIconInfoTooltip from '../../shared/surveyIconInfoTooltip';
import ListWithTooltip from '../../shared/listWithTooltip';
import QuestionnaireChip from '../../shared/questionnaireChip';
import {
  getSurveysHistory,
  clearSurveysHistory,
} from '../../../store/modules/surveysHistory';
import { surveysHistorySelector } from '../../../store/selectors/surveysHistorySelector';
import { useAvailableUsers } from '../../../utility/hooks';
import {
  parseDuplicateParameters,
  parseQueryParams,
  hasNextPage,
  hasSelectedFiltersCheck,
} from '../../../utility/uiUtils';
import {
  isArray,
  isArrayEmpty,
  isObjectEmpty,
  getItemById,
} from '../../../utility/helpers';
import {
  getSurveysHistoryPageFilters,
  getSurveysHistoryTableHeaders,
  prepareSurveysHistoryTableData,
} from '../../../utility/surveyHistory';
import { useTranslations } from '../../../utility/useTranslations';
import { sticky } from '../../../constants/helperCssRules';
import { SURVEYS_HISTORY_INITIAL_SORT } from '../../../constants/surveysHistory';
import { SYSTEM_ROLES } from '../../../constants/roles';
import {
  APP_PAGES,
  PARAMS,
  SURVEYS_HISTORY_DEFAULT_PARAMS,
  PAGE_WHITELISTED_PARAMS,
} from '../../../constants/pages';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  stickyHeader: {
    padding: spacing(8, 0, 6, 0),
    ...sticky(primary.white, 105),
  },
  description: {
    marginBottom: spacing(6),
  },
  filters: {
    marginRight: spacing(2),
  },
  table: {
    '& .grid-table-header': {
      ...sticky(primary.bluish8, 243),
    },
  },
  surveyTitle: {
    display: 'flex',
    alignItems: 'center',
    maxWidth: '100%',
  },
  title: {
    width: 'auto',
  },
  anonymousIcon: {
    display: 'block',
    flexShrink: 0,
    marginTop: spacing(0.5),
    '& svg': {
      width: 16,
      height: 14,
      marginRight: 0,
    },
  },
}));

const SurveysHistoryPage = ({
  location,
  navigate,
  dispatch,
  auth,
  ...rest
}) => {
  const classes = useStyles();
  const translations = useTranslations(APP_PAGES.SURVEYS_HISTORY);
  const { page: defaultPage } = SURVEYS_HISTORY_DEFAULT_PARAMS;
  const { PAGE, ORDERING, CREATED_FOR } = PARAMS;

  const surveysHistory = useSelector(surveysHistorySelector);
  const allUsersParams = useMemo(
    () => ({
      [PARAMS.EXCLUDE_ROLE]: SYSTEM_ROLES.ASSOCIATE,
    }),
    []
  );
  const allUsers = useAvailableUsers(allUsersParams, null);

  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [ordering, setOrdering] = useState(SURVEYS_HISTORY_INITIAL_SORT);
  const [selectedFilters, setSelectedFilters] = useState({
    [CREATED_FOR]: [],
  });

  const filters = getSurveysHistoryPageFilters(translations, allUsers);
  const isActiveFilter = hasSelectedFiltersCheck(selectedFilters);
  const hasLoadMore = hasNextPage(surveysHistory);
  const gridHeaders = getSurveysHistoryTableHeaders(translations.columns);

  const handlePageChange = ({
    newPage = page,
    newOrdering = ordering,
    newFilters = selectedFilters,
    isLoadMore = false,
  } = {}) => {
    const params = {
      [PAGE]: newPage,
      [ORDERING]: newOrdering?.sortKey,
      ...newFilters,
    };

    const query = parseDuplicateParameters(params);

    if (location.search !== `?${query}`) {
      navigate(`/survey-history/?${query}`, { replace: true });
    }

    return getSurveysHistory(dispatch, isLoadMore, params);
  };

  const getInitialData = async () => {
    try {
      const params = parseQueryParams(
        location.search,
        PAGE_WHITELISTED_PARAMS.SURVEYS_HISTORY
      );

      if (!isObjectEmpty(params)) {
        const [ascending, descending] = params[ORDERING].split('-');
        const isAscending = !!ascending;
        const initialOrdering = {
          column: getItemById(
            gridHeaders,
            isAscending ? ascending : descending,
            'sortAs'
          )?.rowKey,
          asc: isAscending,
          sortKey: params[ORDERING],
        };
        const initialFilters = {
          [CREATED_FOR]: params[CREATED_FOR]
            ? [
                ...(isArray(params[CREATED_FOR])
                  ? params[CREATED_FOR]
                  : [params[CREATED_FOR]]),
              ]
            : [],
        };

        setOrdering(initialOrdering);
        setSelectedFilters(initialFilters);
        await Promise.all([
          handlePageChange({
            newOrdering: initialOrdering,
            newFilters: initialFilters,
          }),
        ]);
      } else {
        handlePageChange();
      }
    } finally {
      setIsInitialLoad(false);
    }
  };

  const onFiltersChange = async newFilters => {
    try {
      setIsLoading(true);
      setPage(defaultPage);
      setSelectedFilters(newFilters);
      await handlePageChange({
        newPage: defaultPage,
        newFilters,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onSort = newOrdering => {
    setPage(defaultPage);
    setOrdering(newOrdering);
    handlePageChange({
      newPage: defaultPage,
      newOrdering,
    });
  };

  const onLoadMore = () => {
    setPage(page + 1);
    handlePageChange({
      newPage: page + 1,
      isLoadMore: true,
    });
  };

  const attributeVirtualItemSize = useCallback(
    (index, items) => (index === items.length - 1 ? 28 : 36),
    []
  );

  const onPreviewSurvey = survey => navigate(`/survey-history/${survey.token}`);

  const cleanup = useCallback(() => {
    dispatch(clearSurveysHistory());
  }, [dispatch]);

  useEffect(() => {
    getInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  const renderTitle = survey => {
    return (
      <div className={classes.surveyTitle}>
        <TextBoxWithTooltip
          className={classes.title}
          text={survey.title}
          variant="subtitle1"
        />
        {survey.is_anonymous && (
          <SurveyIconInfoTooltip
            className={classes.anonymousIcon}
            tooltipText={translations.anonymousSurvey}
          />
        )}
      </div>
    );
  };

  const renderAttributes = attributes => (
    <div>
      <ListWithTooltip
        items={attributes}
        visibleItemsCount={3}
        asyncListProps={{
          position: 'top',
          isDisabled: true,
          shouldDisablePortal: false,
          getItemSize: attributeVirtualItemSize,
        }}
      />
    </div>
  );

  const renderQuestionnaire = questionnaire => (
    <QuestionnaireChip name={questionnaire?.name} />
  );

  const renderUsersToReview = subjects => (
    <div>
      <ListWithTooltip
        items={subjects}
        asyncListProps={{
          position: 'top',
          isDisabled: true,
          shouldDisablePortal: false,
        }}
        isUserList
        isReadOnly
      />
    </div>
  );

  const gridTableData = prepareSurveysHistoryTableData(
    surveysHistory.results,
    renderTitle,
    renderAttributes,
    renderQuestionnaire,
    renderUsersToReview
  );

  return (
    <PageContainer
      {...rest}
      translations={translations}
      location={location}
      navigate={navigate}
      auth={auth}
      shouldPassProps={false}
      isFullWidthContent
    >
      {!isInitialLoad && (
        <div>
          <div className={classes.stickyHeader}>
            <div className={classes.description}>
              <Typography variant="body2">
                {translations.description}
              </Typography>
            </div>
            <div className={classes.filtersWrapper}>
              <Filters
                translations={translations.filters}
                selectedFilters={selectedFilters}
                filters={filters}
                onApplyFilters={onFiltersChange}
              />
            </div>
          </div>
          {isArray(surveysHistory.results) &&
            !isArrayEmpty(surveysHistory.results) && (
              <GridTable
                className={classes.table}
                translations={translations}
                initialSort={ordering}
                headers={gridHeaders}
                hasLoadMore={hasLoadMore}
                rows={gridTableData}
                onLoadMore={onLoadMore}
                onSort={onSort}
                onRowClick={onPreviewSurvey}
              />
            )}
          {!isLoading &&
            isArray(surveysHistory.results) &&
            isArrayEmpty(surveysHistory.results) && (
              <NotificationCard
                title={isActiveFilter ? '' : translations.noResults.title}
                content={
                  isActiveFilter
                    ? translations.noResults.search
                    : translations.noResults.message
                }
              />
            )}
        </div>
      )}
    </PageContainer>
  );
};

SurveysHistoryPage.propTypes = {
  location: PropTypes.object.isRequired,
  auth: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
};

export default memo(SurveysHistoryPage);
